// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { createElement, Component, cloneElement, Children } from '../../utils';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import Switch from '../Switch';
import Button from '../Button';
import IconButton from '../IconButton';
import Popover from '../Popover';

import '../../helpers/base.less';
import './SwitchesPanelItem.less';

/**
 * `SwitchesPanelItem` component.
 */
class SwitchesPanelItem extends Component {
    constructor(props) {
        super(props);

        this.state = {
            checked: props.defaultChecked,
        };
    }

    handleChange = checked => {
        this.setState({ checked });

        this.props.onChange(checked);
    };

    render() {
        const {
            onChange,
            defaultChecked,
            intent,
            title,
            description,
            fullDescription,
            baseClassName,
            className,
            switchProps,
            children,
            ...props
        } = this.props;
        const { checked } = this.state;

        return (
            <div
                className={classNames(
                    baseClassName,
                    {
                        [`${baseClassName}--${intent}`]: intent && checked,
                    },
                    className,
                )}
                {...props}
            >
                <h5 className={`${baseClassName}__title`}>{title}</h5>

                {description && <p className={`${baseClassName}__description`}>{description}</p>}

                <div className={`${baseClassName}__actions`}>
                    <Switch
                        className={`${baseClassName}__switch`}
                        intent={intent}
                        onChange={this.handleChange}
                        defaultChecked={defaultChecked}
                        {...switchProps}
                    />

                    {(fullDescription || children) && (
                        <div className={`${baseClassName}__controls`}>
                            {children && Children.map(children, child => {
                                if (child.type === Button) {
                                    return cloneElement(child, {
                                        className: classNames(child.props.className, `${baseClassName}__controls-button`),
                                    });
                                }

                                return child;
                            })}

                            {fullDescription && (
                                <Popover
                                    target={
                                        <IconButton
                                            className={`${baseClassName}__controls-button`}
                                            icon="circle-question-mark"
                                        />
                                    }
                                    placement="bottom"
                                >
                                    {fullDescription}
                                </Popover>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

SwitchesPanelItem.propTypes = {
    /**
     * A callback function, can be executed when the checked state is changing.
     */
    onChange: PropTypes.func.isRequired,
    /**
     * @ignore
     */
    defaultChecked: PropTypes.bool,
    /**
     * Visual intent color to apply to component.
     */
    intent: PropTypes.oneOf(['warning', 'danger']),
    /**
     * Title of the `SwitchesPanelItem`. Must be no longer than two lines.
     */
    title: PropTypes.node.isRequired,
    /**
     * `SwitchesPanelItem` description. Must be no longer than three lines.
     */
    description: PropTypes.node,
    /**
     * Rich full description of the `SwitchesPanelItem`.
     * Adds control button with question mark that shows `Popover` on click.
     */
    fullDescription: PropTypes.node,
    /**
     * Props of underlying [Switch](#!/Switch) component.
     */
    switchProps: PropTypes.object,
    /**
     * Content of the control actions.
     * Don't place here more than three buttons (with the one from 'fullDescription' property) or one link and one button.
     */
    children: PropTypes.node,
    /**
     * @ignore
     */
    className: PropTypes.string,
    /**
     * @ignore
     */
    baseClassName: PropTypes.string,
};

SwitchesPanelItem.defaultProps = {
    defaultChecked: false,
    intent: null,
    description: null,
    fullDescription: null,
    switchProps: {},
    children: null,
    className: null,
    baseClassName: `${CLS_PREFIX}switches-panel-item`,
};

export default SwitchesPanelItem;



// WEBPACK FOOTER //
// ./components/SwitchesPanelItem/SwitchesPanelItem.js